import { useState, useMemo } from 'react';
import AppShell from '@/components/feature/AppShell';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/base/Toast';
import Badge from '@/components/base/Badge';
import { teacherNotifications } from '@/mocks/dashboard';
import { teacherLessonPlans, type LessonPlanRecord } from '@/mocks/lessonPlanList';

type StatusFilter = 'all' | 'draft' | 'submitted' | 'in_progress' | 'completed';
type TabView = 'plans' | 'templates';

const statusBadgeVariant: Record<string, 'secondary' | 'primary' | 'accent' | 'success'> = {
  draft: 'secondary',
  submitted: 'primary',
  in_progress: 'accent',
  completed: 'success',
};

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(dateStr);
}

export default function LessonPlansListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [tab, setTab] = useState<TabView>('plans');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [saveTemplateTarget, setSaveTemplateTarget] = useState<string | null>(null);
  const [templateName, setTemplateName] = useState('');

  const plans = useMemo(() => {
    let filtered = teacherLessonPlans.filter((p) => p.isTemplate === (tab === 'templates'));
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.theme.toLowerCase().includes(q) ||
          p.studentNames.some((n) => n.toLowerCase().includes(q)) ||
          (p.templateName && p.templateName.toLowerCase().includes(q))
      );
    }
    if (tab === 'plans' && statusFilter !== 'all') {
      filtered = filtered.filter((p) => p.status === statusFilter);
    }
    return filtered;
  }, [search, statusFilter, tab]);

  const plansList = plans;
  const templatesList = useMemo(
    () => teacherLessonPlans.filter((p) => p.isTemplate),
    []
  );

  const handleDelete = () => {
    if (!deleteTarget) return;
    toast.success(t('lessonPlans.planDeleted'));
    setDeleteTarget(null);
  };

  const handleDuplicate = (plan: LessonPlanRecord) => {
    toast.success(t('lessonPlans.planDuplicated'));
  };

  const handleNewPlan = () => {
    navigate('/dashboard/teacher-dashboard/lesson-plan');
  };

  const handleEdit = (plan: LessonPlanRecord) => {
    navigate('/dashboard/teacher-dashboard/lesson-plan');
  };

  const handleUseTemplate = (plan: LessonPlanRecord) => {
    navigate('/dashboard/teacher-dashboard/lesson-plan');
  };

  const handleSaveAsTemplate = () => {
    if (!saveTemplateTarget || !templateName.trim()) return;
    toast.success(t('lessonPlans.templateSaved'));
    setSaveTemplateTarget(null);
    setTemplateName('');
  };

  const openSaveTemplate = (plan: LessonPlanRecord) => {
    setSaveTemplateTarget(plan.id);
    setTemplateName(plan.templateName || plan.theme);
  };

  const statusFilters: { key: StatusFilter; labelKey: string }[] = [
    { key: 'all', labelKey: 'lessonPlans.allPlans' },
    { key: 'draft', labelKey: 'common.draft' },
    { key: 'submitted', labelKey: 'lessonPlans.statusSubmitted' },
    { key: 'in_progress', labelKey: 'lessonPlans.statusInProgress' },
    { key: 'completed', labelKey: 'lessonPlans.statusCompleted' },
  ];

  const renderPlanCard = (plan: LessonPlanRecord) => {
    const isTemplate = plan.isTemplate;

    return (
      <div
        key={plan.id}
        className="group bg-background-50 border border-background-200 rounded-xl p-4 hover:border-background-300 transition-all"
      >
        <div className="flex items-start justify-between gap-4">
          {/* Left: content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <h4 className="text-sm font-heading font-700 text-foreground-900 truncate">
                {isTemplate ? plan.templateName : plan.theme}
              </h4>
              {!isTemplate && (
                <Badge variant={statusBadgeVariant[plan.status] as 'secondary' | 'primary' | 'accent'} size="sm">
                  {t(`lessonPlans.status${plan.status.charAt(0).toUpperCase() + plan.status.slice(1).replace('_', '')}`)}
                </Badge>
              )}
              {isTemplate && (
                <Badge variant="secondary" size="sm">
                  {t('lessonPlans.myTemplates')}
                </Badge>
              )}
            </div>

            {!isTemplate && plan.studentNames.length > 0 && (
              <p className="text-xs text-foreground-600 mb-1 flex items-center gap-1.5 flex-wrap">
                <i className="ri-user-line text-foreground-400" />
                <span className="truncate">{plan.studentNames.join(', ')}</span>
                <span className="text-foreground-300 mx-0.5">&middot;</span>
                <Badge variant="secondary" size="sm">
                  {plan.planType === 'individual'
                    ? t('lessonPlans.planTypeIndividual')
                    : t('lessonPlans.planTypeGroup')}
                </Badge>
              </p>
            )}

            <div className="flex items-center gap-2 flex-wrap text-xs text-foreground-500">
              {!isTemplate && plan.dateStart && (
                <>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line text-foreground-400" />
                    {formatDate(plan.dateStart)} — {formatDate(plan.dateEnd)}
                  </span>
                  <span className="text-foreground-300">&middot;</span>
                </>
              )}
              <span className="flex items-center gap-1">
                <i className="ri-list-check-2 text-foreground-400" />
                {t('lessonPlans.stepsCount', { count: plan.stepCount })}
              </span>
              <span className="text-foreground-300">&middot;</span>
              <span className="flex items-center gap-1">
                <i className="ri-bar-chart-2-line text-foreground-400" />
                {t('lessonPlans.metricsCount', { count: plan.metricCount })}
              </span>
              <span className="text-foreground-300">&middot;</span>
              <span className="flex items-center gap-1">
                <i className="ri-lightbulb-line text-foreground-400" />
                {t('lessonPlans.skillsCount', { count: plan.skillCount })}
              </span>
              {!isTemplate && (
                <>
                  <span className="text-foreground-300">&middot;</span>
                  <span className="text-foreground-400">{timeAgo(plan.updatedAt)}</span>
                </>
              )}
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {isTemplate ? (
              <button
                type="button"
                onClick={() => handleUseTemplate(plan)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-500 text-background-50 text-xs font-label font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-copy-line" />
                {t('lessonPlans.useTemplate')}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => handleEdit(plan)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                  title={String(t('common.edit'))}
                >
                  <i className="ri-pencil-line text-sm" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDuplicate(plan)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                  title={String(t('lessonPlans.duplicatePlan'))}
                >
                  <i className="ri-file-copy-line text-sm" />
                </button>
                <button
                  type="button"
                  onClick={() => openSaveTemplate(plan)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer"
                  title={String(t('lessonPlans.saveAsTemplate'))}
                >
                  <i className="ri-bookmark-line text-sm" />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(plan.id)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                  title={String(t('lessonPlans.deletePlan'))}
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <AppShell
      pageTitle={t('lessonPlans.listPageTitle')}
      pageSubtitle={t('lessonPlans.listPageSubtitle')}
      breadcrumbs={[
        { label: t('common.home') },
        { label: t('common.dashboard'), path: '/dashboard/teacher-dashboard' },
        { label: t('lessonPlans.listPageTitle') },
      ]}
      notifications={teacherNotifications}
    >
      <div className="max-w-4xl mx-auto pb-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2 bg-background-100 rounded-full p-1">
            {([
              { key: 'plans' as TabView, label: t('lessonPlans.allPlans'), icon: 'ri-file-list-3-line' },
              { key: 'templates' as TabView, label: t('lessonPlans.myTemplates'), icon: 'ri-bookmark-line' },
            ]).map((tb) => (
              <button
                key={tb.key}
                type="button"
                onClick={() => { setTab(tb.key); setStatusFilter('all'); setSearch(''); }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-label font-medium transition-all cursor-pointer whitespace-nowrap ${
                  tab === tb.key
                    ? 'bg-background-50 text-foreground-900 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className={`${tb.icon} text-sm`} />
                {tb.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={handleNewPlan}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            {t('lessonPlans.newPlan')}
          </button>
        </div>

        {/* Search & Filters (only for plans tab) */}
        {tab === 'plans' && (
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={String(t('lessonPlans.searchPlans'))}
                className="w-full pl-9 pr-4 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              {statusFilters.map((sf) => (
                <button
                  key={sf.key}
                  type="button"
                  onClick={() => setStatusFilter(sf.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-label font-medium border transition-all cursor-pointer whitespace-nowrap ${
                    statusFilter === sf.key
                      ? 'bg-primary-50 border-primary-300 text-primary-700'
                      : 'border-background-200 text-foreground-500 hover:border-background-300 hover:text-foreground-700'
                  }`}
                >
                  {t(sf.labelKey)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Plans List */}
        {plansList.length === 0 ? (
          <div className="bg-background-50 border border-dashed border-background-300 rounded-xl p-12 text-center">
            <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className={`${tab === 'templates' ? 'ri-bookmark-line' : 'ri-file-list-3-line'} text-foreground-400 text-2xl`} />
            </div>
            <p className="text-sm font-medium text-foreground-700 mb-1">
              {tab === 'templates' ? t('lessonPlans.noTemplates') : t('lessonPlans.noLessonPlans')}
            </p>
            <p className="text-xs text-foreground-500 mb-5">
              {tab === 'templates' ? t('lessonPlans.noTemplatesDesc') : t('lessonPlans.noLessonPlansDesc')}
            </p>
            <button
              type="button"
              onClick={handleNewPlan}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-add-line" />
              {t('lessonPlans.newPlan')}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {plansList.map(renderPlanCard)}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
            <div className="relative bg-background-50 rounded-xl shadow-floating p-6 w-full max-w-sm mx-4">
              <h4 className="font-heading font-700 text-foreground-900 mb-2">{t('lessonPlans.deletePlan')}</h4>
              <p className="text-sm text-foreground-600 mb-5">{t('lessonPlans.deletePlanConfirm')}</p>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setDeleteTarget(null)}
                  className="px-4 py-2 text-sm font-label font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-red-600 transition-colors cursor-pointer"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Save as Template Modal */}
        {saveTemplateTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm" onClick={() => setSaveTemplateTarget(null)} />
            <div className="relative bg-background-50 rounded-xl shadow-floating p-6 w-full max-w-sm mx-4">
              <h4 className="font-heading font-700 text-foreground-900 mb-1">{t('lessonPlans.saveAsTemplateTitle')}</h4>
              <p className="text-xs text-foreground-500 mb-4">{t('lessonPlans.noTemplatesDesc')}</p>
              <div className="mb-5">
                <span className="text-xs font-label font-medium text-foreground-600 mb-1.5 block">{t('lessonPlans.templateName')}</span>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder={String(t('lessonPlans.templateNamePlaceholder'))}
                  className="w-full xl-input text-sm"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveAsTemplate(); }}
                />
              </div>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setSaveTemplateTarget(null)}
                  className="px-4 py-2 text-sm font-label font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleSaveAsTemplate}
                  disabled={!templateName.trim()}
                  className="px-4 py-2 bg-accent-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}